import { ClockCounterClockwise } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDocument, useRevisions } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { ShareGrantsPanel } from "~/features/documents/ShareGrantsPanel";
import { DivergenceBadge } from "~/features/merge/DivergenceBadge";
import { MergePanel } from "~/features/merge/MergePanel";
import { RevisionParts } from "~/features/merge/RevisionParts";
import { Avatar } from "~/foundation/components/Avatar";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { cn } from "~/foundation/utils/cn";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * W9 §A.2 — extended into the merge surface: the divergence badge, "Contributors by part"
 * with a per-part/whole-revision restore, and the merge panel itself (open · preview ·
 * resolve · commit · audit). `?merge=<sessionId>` keeps an open merge linkable, the same way
 * `?revision=` already keeps a selected revision linkable.
 *
 * §A.3.2 — also carries the grant-management panel slate adds (kms has no UI for
 * `share-grants.*` at all); see `ShareGrantsPanel` for why it lives here rather than in the
 * shell's share dialog.
 */
export function DocumentHistoryScene({
  documentId,
  revisionId,
  mergeSessionId,
}: {
  documentId: string;
  revisionId?: string;
  mergeSessionId?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: document } = useDocument(documentId);
  const { data: revisions, isLoading } = useRevisions(documentId);
  useDocumentTitle(document ? `${t("History")} · ${document.title}` : t("History"));

  const selected = revisions?.find((r) => r.id === revisionId) ?? revisions?.[0];
  const canUpdate = document?.abilities[ABILITY.documentUpdate] === true;
  const canShare = document?.abilities[ABILITY.documentShare] === true;

  const setMergeParam = (sessionId: string | null) => {
    void navigate({
      to: "/documents/$documentId/history",
      params: { documentId },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...(sessionId ? { merge: sessionId } : {}),
      }),
      replace: true,
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div>
        <Link
          to="/documents/$documentId"
          params={{ documentId }}
          className="text-small text-text-tertiary hover:text-text"
        >
          ← {document?.title ?? t("Document")}
        </Link>
        <Text as="h1" variant="h2" className="mt-1">
          {t("History")}
        </Text>
      </div>

      <DivergenceBadge
        documentId={documentId}
        canUpdate={canUpdate}
        onMerge={() => setMergeParam("")}
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={t("Loading")} />
        </div>
      ) : !revisions?.length ? (
        <Empty icon={ClockCounterClockwise} message={t("No revisions recorded yet")} />
      ) : (
        <div className="flex flex-col gap-4 md:flex-row">
          <ul className="flex flex-col rounded-md border border-divider md:w-64">
            {revisions.map((revision) => (
              <li key={revision.id} className="border-b border-divider last:border-b-0">
                <Link
                  to="/documents/$documentId/history"
                  params={{ documentId }}
                  search={{ revision: revision.id }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 hover:bg-hover-bg",
                    selected?.id === revision.id && "bg-sidebar-active-bg",
                  )}
                >
                  <Avatar
                    name={revision.createdBy.name}
                    src={revision.createdBy.avatarUrl}
                    size={24}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body text-text">
                      {revision.createdBy.name}
                    </span>
                    <span className="block truncate text-small text-text-tertiary">
                      {formatDateTime(revision.createdAt, i18n.language)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="min-w-0 flex-1 rounded-md border border-divider p-4">
            {selected ? (
              <>
                <Text as="h2" variant="h3">
                  {selected.title}
                </Text>
                <Text variant="secondary" className="mt-2">
                  {selected.summary}
                </Text>
                {/* SLATE-LATER (§15): a real diff needs the editor's document model. */}
                <Text variant="small" className="mt-4">
                  {t("A visual diff arrives with the editor.")}
                </Text>
                <RevisionParts
                  documentId={documentId}
                  revisionId={selected.id}
                  canRestore={canUpdate}
                />
              </>
            ) : null}
          </div>
        </div>
      )}

      <ShareGrantsPanel documentId={documentId} canShare={canShare} />

      {mergeSessionId !== undefined ? (
        <MergePanel
          documentId={documentId}
          revisions={revisions ?? []}
          sessionId={mergeSessionId || null}
          onSessionOpened={(sessionId) => setMergeParam(sessionId)}
          onClose={() =>
            void navigate({
              to: "/documents/$documentId/history",
              params: { documentId },
              search: (prev: Record<string, unknown>) => {
                const { merge: _merge, ...rest } = prev;
                return rest;
              },
              replace: true,
            })
          }
        />
      ) : null}
    </div>
  );
}
